<template>
  <div class="home">
    <!-- SUCHE (nur mobil — der Desktop hat die Seitenleiste) -->
    <HomeSearch />

    <!-- HERO -->
    <v-card flat class="hero-card mb-8 d-none d-lg-block">
      <div class="hero-kicker font-mono">
        Tagesreport<span v-if="date"> · {{ toDate(date) }}</span>
      </div>
      <h1 class="hero-title">Pünktlichkeit<br>auf Knopfdruck.</h1>
      <p class="hero-sub">
        Jede Fernverkehrsfahrt der Deutschen Bahn — ausgewertet, sortiert und
        auf die Minute genau erfasst.
      </p>
      <div class="hero-chips" v-if="worstTrip">
        <v-chip
          :to="{ name: 'train', params: { trainNumber: worstTrip.trainNumber } }"
          color="white"
          variant="flat"
          class="hero-chip"
          append-icon="mdi-chevron-right"
        >
          <v-icon start size="small">mdi-trending-up</v-icon>
          Größte Verspätung am {{ toDate(date) }}:&nbsp;
          <strong>{{ worstTrip.category }} {{ worstTrip.trainNumber }}</strong>
          &nbsp;{{ formatDelay(Math.round(worstDelay), false) }}
        </v-chip>
      </div>
    </v-card>

    <!-- ZULETZT ANGESEHEN -->
    <RecentVisits />

    <!-- FAVORITEN -->
    <section class="mb-8">
      <div class="section-head">
        <span class="accent-dot" />
        <h2>Favoriten</h2>
      </div>
      <div v-if="favorites">
        <v-expansion-panels v-if="favorites.length > 0">
          <template v-for="fav in favorites" :key="fav.trip.trainNumber">
            <Trip :route="item" :trip="fav.trip" v-for="item in fav.history" :key="item.id" />
          </template>
        </v-expansion-panels>
        <v-sheet v-else class="empty-card" rounded="lg">
          <v-icon size="30" color="primary" class="mb-2">mdi-star-outline</v-icon>
          <div class="font-weight-medium">Es sind noch keine Favoriten definiert.</div>
          <div class="text-medium-emphasis text-body-2 mt-1">
            Öffne einen Zug und markiere ihn als Favorit, um ihn hier zu sehen.
          </div>
        </v-sheet>
      </div>
      <LoadingIndicator v-else />
    </section>

    <!-- TOP-VERSPÄTUNGEN -->
    <section v-if="leaderboard">
      <div class="section-head" v-if="hasLeaderboard">
        <span class="accent-dot" />
        <h2>Top-Verspätungen</h2>
        <span class="section-when font-mono" v-if="date">{{ toDate(date) }}</span>
      </div>
      <v-expansion-panels v-if="hasLeaderboard">
        <Trip :route="item" :lazy="true" :showDate="false" v-for="item in leaderboard" :key="item.id" />
      </v-expansion-panels>
    </section>
    <section v-else-if="err">
      <div class="section-head">
        <span class="accent-dot" />
        <h2>Leaderboard nicht verfügbar (#{{ err.response.status }})</h2>
      </div>
    </section>
    <LoadingIndicator v-else />
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import { mapState } from 'vuex'
import sortBy from 'lodash/sortBy';
import DateTimeMixin from '@/mixins/DateTimeMixin'
import DelayMixin from '@/mixins/DelayMixin'
import TitleMixin from '@/mixins/TitleMixin'
import LoadingIndicator from '@/components/LoadingIndicator.vue'
import Trip from '@/components/Trip.vue'
import HomeSearch from '@/components/HomeSearch.vue'
import RecentVisits from '@/components/RecentVisits.vue'

export default {
  name: 'Home',

  components: {
    LoadingIndicator,
    Trip,
    HomeSearch,
    RecentVisits,
  },

  mixins: [DateTimeMixin, DelayMixin, TitleMixin],

  computed: {
    hasLeaderboard: function() {
      return this.leaderboard != null && this.leaderboard.length > 0;
    },

    worstTrip: function() {
      return this.hasLeaderboard ? this.leaderboard[0].trip : null;
    },

    worstDelay: function() {
      return this.hasLeaderboard
        ? this.leaderboard[0].delayStatistics?.weightedAverageDelay
        : null;
    },

    title: function() {
      return import.meta.env.VITE_NAME;
    },

    ...mapState({
      favoriteTrains: function(state) {
        return [...state.favoriteTrains].sort()
      },
    }),
  },

  data: () => ({
    date: null,
    err: null,
    favorites: null,
    leaderboard: null,
  }),

  methods: {
    getFavorites: function() {
      this.favorites = null
      this.err = null

      var cutoff = DateTime.local().setZone("Europe/Berlin").minus({days: 1})
      var favoriteTrains = this.favoriteTrains

      if (favoriteTrains.length > 0) {
        this.$log.info(`Retrieving favorites @ ${cutoff.toISODate()}`)
        this.$api
          .get(`/train/${favoriteTrains.join(',')}/history?from=${cutoff.toISODate()}`)
          .then(response => {
            this.favorites = response.data

            this.favorites.forEach(favorite => {
              favorite.history.splice(1, favorite.history.length - 1)
            })
            this.favorites = sortBy(this.favorites, [function(fav) { return fav.trip.trainNumber }])
          })
          .catch(err => {
            this.err = err
          })
      } else {
        this.$log.info('No favorites defined')
        this.favorites = []
      }
    },

    getLeaderboard: function() {
      this.leaderboard = null
      this.err = null

      var now = DateTime.local().setZone("Europe/Berlin");
      var cutoff = now.set({hour: 8, minute: 30, second: 0})

      if (now > cutoff) {
        this.date = now.minus({days: 1})
      } else {
        this.date = now.minus({days: 2})
      }

      this.$log.info(`Retrieving leaderboard for ${this.date.toISODate()}`);
      this.$api
        .get(`/delays/leaderboard?date=${this.date.toISODate()}&size=10`)
        .then(response => {
          // The leaderboard uses the Summary view (no stops). Rather than
          // eagerly fetching the Details view for every trip up front — which
          // made the home page slow — each Trip is rendered with :lazy and
          // loads its own stops the first time it is expanded.
          this.leaderboard = response.data
        })
        .catch(err => {
          this.err = err
        });
    },
  },

  mounted: function () {
    this.getFavorites()
    this.getLeaderboard()
  },
};
</script>

<style scoped>
.hero-card {
  color: #fff;
  padding: 34px 36px;
  border-radius: 18px !important;
  background:
    radial-gradient(680px 240px at 108% -45%, rgba(255, 255, 255, 0.13), transparent 60%),
    linear-gradient(100deg, #000080, #15157a);
}
.hero-kicker {
  font-size: 0.78rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #c9caf5;
}
.hero-title {
  font-size: clamp(28px, 4.5vw, 44px);
  font-weight: 800;
  line-height: 1.03;
  letter-spacing: -0.025em;
  margin: 12px 0 0;
}
.hero-sub {
  color: #d7d8f3;
  margin: 14px 0 0;
  max-width: 52ch;
  line-height: 1.55;
}
.hero-chips { margin-top: 22px; }
.hero-chip {
  font-weight: 600;
  color: #000080 !important;
  /* Wrap and grow in height instead of overflowing on narrow screens.
     !important beats Vuetify's fixed .v-chip height / white-space. */
  height: auto !important;
  min-height: 32px;
  max-width: 100%;
  white-space: normal !important;
}
.hero-chip :deep(.v-chip__content) {
  white-space: normal;
  padding-block: 5px;
  line-height: 1.35;
}
@media (max-width: 599px) {
  .hero-chip { font-size: 0.8rem; }
}

.section-head {
  display: flex;
  align-items: center;
  gap: 11px;
  margin: 0 4px 14px;
}
.section-head h2 {
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
}
.accent-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: rgb(var(--v-theme-accent));
}
.section-when {
  margin-left: auto;
  font-size: 0.8rem;
  opacity: 0.55;
}

.empty-card {
  text-align: center;
  padding: 36px 22px;
  border: 1px dashed #cfd5ea;
  background: transparent;
}
</style>
